import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/atoms/Button';
import { OnboardingStepWrapper } from 'components/organisms/OnboardingStepLayout';
// todo: solve deps cycle
import StepsProgressSummary from './StepsProgressSummary';
import type { AvailableCountries } from 'types/users.types';
import type { StepMode } from '../utils/stepDefinitions';
import type { ActivationStepProps } from '../ActivationFlow/types';

interface InitActivationStepProps extends ActivationStepProps {
  /** Override substep ID used to calculate progress statuses (defaults to 'init_activation') */
  summaryCurrentSubstepId?: string;
  /** Filter summary to only show steps of a given mode (e.g. pre-account only) */
  summaryStepsScope?: StepMode;
  /** User's country — forwarded to summary to exclude inapplicable country-specific steps */
  summaryUserCountry?: AvailableCountries;
}

/**
 * Initial activation step showing checklist of completed onboarding steps
 * This is the entry point for the post-account activation flow
 *
 * Now uses dynamic ActivationProgressSummary component to show all main steps
 * with their completion status based on current flow position
 *
 * Also reused in pre-account flow (with summaryStepsScope=PreAccount) to show
 * a summary of pre-account steps after the intro slides
 */
const SummaryStep: FC<InitActivationStepProps> = ({
  onContinue,
  summaryCurrentSubstepId,
  summaryStepsScope,
  summaryUserCountry,
  // Wrapper props (automatically injected)
  progressSegments,
  onProgressSegmentClick,
  title,
  subtitle,
  icon,
  showProgressBar,
  showBackButton,
  onBack,
  isUpdatingStep,
}) => {
  const { t } = useTranslation();

  return (
    <OnboardingStepWrapper
      showProgressBar={showProgressBar}
      progressSegments={progressSegments}
      onProgressSegmentClick={onProgressSegmentClick}
      icon={icon}
      title={title}
      subtitle={subtitle}
      showBackButton={showBackButton}
      onBack={onBack}
    >
      <div className="my-5">
        <StepsProgressSummary
          currentSubstepId={summaryCurrentSubstepId ?? 'init_activation'}
          mode="all"
          stepsScope={summaryStepsScope}
          userCountry={summaryUserCountry}
        />
      </div>

      <Button
        onClick={onContinue}
        className="w-full mt-3"
        color="bank"
        disabled={isUpdatingStep}
      >
        {t('continue')}
      </Button>
    </OnboardingStepWrapper>
  );
};

export default SummaryStep;
